/**
 * @file     parallaxBackground.ts
 * @purpose  The app shell's faint brand-tinted background gradient for one
 *           palette. Emitted per theme in the pre-paint boot CSS as
 *           --r-parallax-bg, so the server-rendered shell follows the
 *           visitor's theme before React hydrates.
 */
import type { createDesignTheme } from '@/design-system/theme';

type Palette = ReturnType<typeof createDesignTheme>['palette'];

const alpha = (color: string, opacity: number): string => {
  if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacity})`);
  const hex = color.replace('#', '');
  const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16);
  const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.slice(2, 4), 16);
  const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

export function parallaxBackground(palette: Palette): string {
  return [
    `radial-gradient(ellipse 120% 80% at 35% 40%, ${alpha(palette.brand.primary, 0.025)} 0%, transparent 70%)`,
    `radial-gradient(circle 900px at 12% 6%, ${alpha(palette.brand.primary, 0.07)} 0%, transparent 100%)`,
    `radial-gradient(ellipse 55% 70% at 78% 22%, ${alpha(palette.brand.accent, 0.06)} 0%, transparent 100%)`,
    `radial-gradient(ellipse 90% 45% at 18% 82%, ${alpha(palette.accent[600], 0.04)} 0%, transparent 100%)`,
    `radial-gradient(circle 350px at 62% 11%, ${alpha(palette.brand.primary, 0.08)} 0%, ${alpha(palette.brand.primary, 0.015)} 60%, transparent 100%)`,
    `radial-gradient(ellipse 35% 50% at 92% 55%, ${alpha(palette.brand.secondary, 0.03)} 0%, transparent 100%)`,
    `linear-gradient(135deg, ${alpha(palette.brand.primary, 0.02)} 0%, transparent 40%, transparent 60%, ${alpha(palette.accent[600], 0.015)} 100%)`,
  ].join(', ');
}
